// Gross-up helper: how much REVENUE a single-member LLC in Deschutes County,
// Oregon must produce so its owner keeps a given net (take-home) amount after
// taxes. Oregon has no county/local income tax and no sales tax, so a Deschutes
// County LLC's tax load is the federal self-employment tax plus federal and
// Oregon personal income tax on the pass-through profit. We reuse the app's
// existing 2025 tax engine and solve the inverse (net → gross) by binary search
// since the brackets are progressive.
import { calculateIncomeDetails, IncomeInfo } from "./taxCalculatorUtils";

export interface LlcRevenueEstimate {
  netMonthly: number; // the take-home target, per month
  grossMonthly: number; // revenue needed, per month
  grossAnnual: number; // revenue needed, per year
  taxMonthly: number; // taxes owed, per month
  effectiveRate: number; // total tax / gross revenue (0..1)
}

// Assumptions used for the estimate. Deschutes County / Oregon, single filer,
// self-employed with no separately deducted business expenses (the revenue is
// the profit), standard deduction, no dependents.
const buildIncomeInfo = (annualGross: number): IncomeInfo => ({
  incomeSource: "self_employed",
  incomeSources: [
    {
      id: "llc",
      revenueSource: "LLC",
      paymentType: "salary",
      paymentAmount: annualGross,
      hoursWorkedWeekly: 0,
    },
  ],
  businessDeductions: 0,
  state: "OR",
  filingStatus: "single",
  numChildren: 0,
});

// Net take-home for a given annual gross revenue (self-employment profit).
const netForGross = (annualGross: number): number =>
  calculateIncomeDetails(buildIncomeInfo(annualGross)).yearlyIncome.netIncome;

/**
 * Solve for the annual gross revenue whose after-tax net equals `netAnnual`.
 * Binary search: net(gross) is monotonically increasing in gross.
 */
const grossForNetAnnual = (netAnnual: number): number => {
  if (netAnnual <= 0) return 0;
  let lo = netAnnual; // gross is always >= net
  let hi = netAnnual * 3 + 10000; // generous upper bound
  // Expand hi until it over-covers the target (guards very high brackets).
  while (netForGross(hi) < netAnnual && hi < 1e12) hi *= 2;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (netForGross(mid) < netAnnual) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
};

/**
 * Given a monthly take-home target, return the LLC revenue required to net it.
 * Taxes are computed on the annualized figure (brackets are annual) then
 * folded back to a monthly number.
 */
export const estimateLlcRevenueForNetMonthly = (
  netMonthly: number
): LlcRevenueEstimate => {
  const netAnnual = Math.max(0, netMonthly) * 12;
  const grossAnnual = grossForNetAnnual(netAnnual);
  const taxAnnual = grossAnnual - netAnnual;
  return {
    netMonthly,
    grossMonthly: grossAnnual / 12,
    grossAnnual,
    taxMonthly: taxAnnual / 12,
    effectiveRate: grossAnnual > 0 ? taxAnnual / grossAnnual : 0,
  };
};
